import {
  Button,
  css,
  FormField,
  Heading,
  Modal,
  Paragraph,
  SnapshotIcon,
  styled,
  TextInput,
} from 'canoe';
import { Field, FieldInputProps, FieldMetaProps, Form, Formik } from 'formik';
import { compose, connectWithHammock, ENDPOINTS } from 'hammock';
import React, { useState } from 'react';
import * as Yup from 'yup';

import { ContentPlacement, URLOptions } from '@candu-ai/placement/types';
import { Snapshot } from '@candu-ai/snapshots/types';
import { useAuthentication } from 'commons/hooks';
import { retakeSnapshot } from 'craft/containers/Toolbox/tabs/Placements/utils';
import { useExtensionInstallationState } from 'dashboard/containers/ExtensionStateProvider';
import { NewSnapshotModalExtensionPrompt } from 'dashboard/pages/Content/pages/Snapshots/NewSnapshotModalExtensionPrompt';

const validationSchema = Yup.object().shape({
  url: Yup.string()
    .required()
    .matches(/^(https?:\/\/)?.*\..+(\..+)*/, 'Enter a valid URL'),
});

const FormRow = styled.div`
  ${({ theme }) => css`
    display: flex;
    gap: ${theme.spaces.xs};
  `}
`;

interface Props {
  onClose: (isSuccess?: boolean) => void;
  contentId?: string;
  snapshots?: Snapshot[];
  placements?: ContentPlacement[];
  forceOpen?: boolean;
  activePlacement?: ContentPlacement;
}

const AddSnapshotModalImpl = ({
  onClose,
  contentId,
  snapshots,
  placements,
  forceOpen,
  activePlacement,
}: Props) => {
  const { applicationId } = useAuthentication();
  const [stepIndex, setStepIndex] = useState(forceOpen && !!snapshots?.length ? 1 : 0);
  const { hasInstalledChromeExtension } = useExtensionInstallationState();

  const snapshotURL =
    placements?.find((placement) => placement.urls?.[0]?.operation.type === URLOptions.EXACTLY)
      ?.urls?.[0]?.operation.operand ?? '';

  const onGrabSnapshot = (targetUrl: string) => {
    if (!applicationId || targetUrl === '' || !contentId) {
      return;
    }

    onClose(true);
    retakeSnapshot({ applicationId, contentId, targetUrl, placement: activePlacement });
  };

  // only show this modal if there are no snapshots and the flag is on
  if (!forceOpen && snapshots?.length) {
    return null;
  }

  return (
    <Modal onClose={onClose}>
      <Modal.Body>
        {stepIndex === 0 && (
          <>
            <Heading size="S" align="center" mt="normal" mb="normal">
              Editing with Snapshots
            </Heading>
            <Paragraph size="short" color="OnyxGrayL10">
              With Snapshots you can edit your content and see what it will look like in your
              product at the the same time.
            </Paragraph>
          </>
        )}

        <Formik<{
          url: string;
        }>
          validateOnMount
          initialValues={{
            url: snapshotURL,
          }}
          validationSchema={validationSchema}
          onSubmit={({ url }) => onGrabSnapshot(url)}
        >
          {({ isSubmitting, isValid, handleSubmit }) => (
            <>
              {stepIndex === 1 && (
                <>
                  <Heading size="S" align="center" mt="normal" mb="normal">
                    Where will the overlay be displayed?
                  </Heading>
                  <Paragraph size="short" color="OnyxGrayL10" maxWidth="none">
                    We’ll use this URL to grab a Snapshot, which will then be used in the Candu
                    Editor.
                  </Paragraph>
                  <Form
                    data-cy="retake-snapshot-form"
                    onKeyDown={(e: KeyboardEvent) => {
                      if (isValid && e.key === 'Enter') {
                        handleSubmit();
                      }
                    }}
                  >
                    <FormRow>
                      <Field name="url">
                        {({
                          field,
                          meta,
                        }: {
                          field: FieldInputProps<string>;
                          meta: FieldMetaProps<string>;
                        }) => (
                          <FormField
                            feedback={meta.touched ? meta.error : ''}
                            state={meta.touched && meta.error ? 'danger' : 'none'}
                          >
                            <TextInput {...field} {...meta} placeholder="your-app.io/dashboard" />
                          </FormField>
                        )}
                      </Field>

                      <Button
                        variant="secondary"
                        icon={SnapshotIcon}
                        loading={isSubmitting}
                        disabled={!isValid}
                        mt="xs"
                        onClick={() => {
                          if (hasInstalledChromeExtension) {
                            handleSubmit();
                          } else {
                            setStepIndex(2);
                          }
                        }}
                      >
                        Grab Snapshot
                      </Button>
                    </FormRow>
                  </Form>
                </>
              )}

              {stepIndex === 2 && (
                <NewSnapshotModalExtensionPrompt
                  onClose={() => onClose()}
                  onSubmit={() => handleSubmit()}
                />
              )}
            </>
          )}
        </Formik>
      </Modal.Body>
      {stepIndex === 0 && (
        <Modal.Footer
          justify="space-between"
          secondaryAction={{
            children: 'No, thanks',
            onClick: () => {
              onClose();
            },
          }}
          primaryAction={{
            icon: SnapshotIcon,
            children: 'Add a Product Snapshot to Content',
            variant: 'secondary',
            onClick: () => {
              setStepIndex(1);
            },
          }}
        />
      )}
    </Modal>
  );
};

const withSnapshots = connectWithHammock(({ contentId }) => ({
  endpoint: ENDPOINTS.snapshots,
  getResourceOnMount: !!contentId,
  query: { finder: 'byContentId', id: contentId },
  resourceName: 'snapshots',
  refetchInterval: false,
  refetchOnWindowFocus: false,
}));

const withPlacements = connectWithHammock(({ contentId }) => ({
  endpoint: ENDPOINTS.contentPlacements,
  getResourceOnMount: !!contentId,
  query: { finder: 'byContentId', contentId },
  resourceName: 'placements',
}));

export const AddSnapshotModal = compose(withSnapshots, withPlacements)(AddSnapshotModalImpl);
